import { checkIfWorkEmail, AwsUser } from '@auth/common/util';
import auth from '@services/cognito';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import Button from '@ui-library/button';
import Svg from '@svg/index';
import { Flex } from '@ui-library/misc';
import Amplify, { Auth, Hub } from 'aws-amplify';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth/lib/types';
import { awsConfig, WEB_APP_URL, disableCognito, disableSSO } from '@config';
import { LabelMedium } from '@ui-library/typography';
import theme from '@css/theme';
import Container from '@auth/common/container';
import { BorderText, ContentView, CustomText, TitleText } from '@auth/common/styles';
import classes from '@auth/common/common.module.scss';
import { CatchErr } from '@utils/types';
import { SIGNUP_ERROR_MESSAGES } from '@auth/common/constants';
import useStores from '@stores/useStores';
import LocalStorage from '@utils/localStorage';

interface Props {
  removeFullVw?: boolean;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default ({ removeFullVw }: Props) => {
  const location = useLocation();
  const history = useHistory();

  const { userStore } = useStores();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const cognitoInitialize = () => {
    awsConfig.Auth.oauth.redirectSignIn = WEB_APP_URL + location.pathname;
    awsConfig.Auth.oauth.redirectSignOut = WEB_APP_URL + location.pathname;
    Amplify.configure(awsConfig);
  };

  useEffect(() => {
    if (userStore.loggedIn) {
      history.push('/');
    }
    if (!disableCognito) {
      cognitoInitialize();
    }

    setErrorMessage(LocalStorage.get('authError') || '');

    // Google authentication
    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
          handleAuth(data);
          break;
        case 'signOut':
          break;
      }
    });

    return () => {
      LocalStorage.remove('authError');
    };
  }, []);

  const handleAuth = async (user: AwsUser | null) => {
    setErrorMessage('');
    setLoading(true);
    LocalStorage.remove('authError');

    try {
      if (user) {
        const loginResp = user.signInUserSession;
        const { name, email, 'custom:userId': userId } = loginResp.getIdToken().payload;

        if (!userId && !checkIfWorkEmail(email)) {
          throw { errorMessage: SIGNUP_ERROR_MESSAGES.SIGNUP_TO_CONTINUE };
        }

        const provider = loginResp.getIdToken().payload.identities[0].providerName;
        const providerUserId = user.getUsername();
        const tokens = auth.getTokens(loginResp);

        const resp = await auth.authSignin(tokens.idToken, { name, provider, freetrial: true });

        auth.sendAnalytics(resp.firstLogin, resp.email);
        await userStore.loginUser(tokens);
        await userStore.trackUserLogin({ firstTimeLogin: resp.firstLogin });
        await userStore.setProvider(provider, providerUserId);
        history.push('/');

        window.rudderanalytics.track('Login with google response', {
          email: resp.email,
          name: resp.name,
          isLoginSuccessful: true,
          isExistingUser: !resp.firstLogin,
        });
      }
    } catch (error: CatchErr) {
      const message =
        error.response?.data?.message || error?.errorMessage || 'Something went wrong';

      setLoading(false);
      setErrorMessage(message);
      window.rudderanalytics.track('Login with google response', {
        email: user?.signInUserSession?.getIdToken()?.payload?.email || 'email',
        isError: true,
        errorMessage: message,
      });

      Auth.signOut();
      LocalStorage.set('authError', message);
    }
  };

  const handleLoginEmail = () => {
    window.rudderanalytics.track('Login with email', {});
    history.push('/elogin');
  };

  const onGoogleClick = () => {
    window.rudderanalytics.track('Login with google', {});
    Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google });
  };

  const onSignupClick = () => {
    window.rudderanalytics.track('Signup', {});
  };

  const onSsoClick = () => {
    window.rudderanalytics.track('Continue with SSO', {});
  };

  const redirectBlendo = () => (location.pathname.includes('blendo') ? 'blendoSignup' : 'signup');

  const antIcon = <LoadingOutlined style={{ fontSize: 42 }} spin />;

  return (
    <Container showLogoInForm removefullVw={removeFullVw}>
      <ContentView>
        {loading ? (
          <Spin indicator={antIcon} />
        ) : (
          <div>
            <TitleText className="m-b-md">Log in</TitleText>
            <div className="text-center">
              <Flex flexDirection="column">
                <Button
                  secondary
                  onClick={handleLoginEmail}
                  className={`${classes.inputBtn} m-b-md m-t-md`}
                >
                  <CustomText>Log in with Email & Password</CustomText>
                </Button>
                <Button
                  {...(LocalStorage.get('authError') === null
                    ? { secondary: true }
                    : { deleteOutlined: true })}
                  onClick={onGoogleClick}
                  svg={{
                    name: 'google',
                    align: 'start',
                  }}
                  className={classes.inputBtn}
                >
                  <CustomText className="m-l-md">Log in with Google</CustomText>
                </Button>
              </Flex>
            </div>
            {errorMessage.length > 1 && (
              <div style={{ width: '350px' }} className="m-v-md">
                <Svg style={{ float: 'left', marginRight: '0.25rem' }} name="error" />
                <LabelMedium color={theme.color.error}>{errorMessage}</LabelMedium>
              </div>
            )}

            {!disableSSO && (
              <BorderText className="m-t-md">
                <Link className="link-blue" onClick={onSsoClick} to="/sso">
                  Continue with SSO instead
                </Link>
              </BorderText>
            )}

            <CustomText className="m-t-sd" fontSize="13px" lineHeight="22px">
              Don&#39;t have an account?&nbsp;
              <Link className="link-blue" onClick={onSignupClick} to={redirectBlendo()}>
                Sign up
              </Link>
            </CustomText>
          </div>
        )}
      </ContentView>
    </Container>
  );
};
